import React from 'react';
import { inlineActionButtonTemplate } from '../theme';
import { getExpirationRangeLabel, getDaysToExitPercent } from '../utils/presetMappings';

function QueuedScenarioCard({ scenario, index, onRemove, onLoad, onSave, showDetails = false }) {
  // Generate summary label
  const generateSummary = () => {
    const tickers = Array.isArray(scenario.tickers)
      ? scenario.tickers.join(', ')
      : scenario.tickers;
    const position = scenario.position_type === 'sell' ? 'Sell' : 'Buy';
    const type = scenario.option_type === 'call' ? 'Calls' : 'Puts';
    const strike = scenario.strike_pct ? `${(scenario.strike_pct * 100).toFixed(0)}%` : '';
    return `${tickers} ${position} ${type} ${strike}`;
  };

  // Generate details text (for non-detailed view)
  const generateDetails = () => {
    const exp = `${getExpirationRangeLabel(scenario.days_until_exp)} exp`;
    const gain = `${(scenario.stock_gain_pct * 100).toFixed(0)}% move`;
    const hold = `${getDaysToExitPercent(scenario.days_to_gain, scenario.days_until_exp, scenario._exitPercent)} exit`;
    return `${exp} • ${gain} • ${hold}`;
  };

  // Format strike position with ITM/ATM/OTM label
  const formatStrikePosition = () => {
    const pct = (scenario.strike_pct * 100).toFixed(0);
    const sign = scenario.strike_pct > 0 ? '+' : '';
    let moneyness = 'ATM';

    if (scenario.option_type === 'call') {
      if (scenario.strike_pct < -0.02) moneyness = 'ITM';
      else if (scenario.strike_pct > 0.02) moneyness = 'OTM';
    } else { // put
      if (scenario.strike_pct > 0.02) moneyness = 'ITM';
      else if (scenario.strike_pct < -0.02) moneyness = 'OTM';
    }

    return `${sign}${pct}% ${moneyness}`;
  };

  // Format expected move
  const formatExpectedMove = () => {
    const pct = (scenario.stock_gain_pct * 100).toFixed(0);
    const sign = scenario.stock_gain_pct > 0 ? '+' : '';
    return `${sign}${pct}%`;
  };

  if (!showDetails) {
    // Original compact view
    return (
      <div className="queue-item">
        <div className="queue-item-info">
          <div className="queue-item-label">{generateSummary()}</div>
          <div className="queue-item-details">{generateDetails()}</div>
        </div>
        <div className="queue-item-actions">
          <button
            onClick={onLoad}
            className={`${inlineActionButtonTemplate.cssClasses.base} ${inlineActionButtonTemplate.cssClasses.primary}`}
            title="Load this scenario into the form"
          >
            Load
          </button>
          <button
            onClick={onRemove}
            className={`${inlineActionButtonTemplate.cssClasses.base} ${inlineActionButtonTemplate.cssClasses.danger}`}
            title="Remove from queue"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  // Enhanced detailed view - Strategy card style
  const tickers = Array.isArray(scenario.tickers)
    ? scenario.tickers.join(', ')
    : scenario.tickers;
  const isCall = scenario.option_type === 'call';
  const isSell = scenario.position_type === 'sell';
  const positionLabel = isSell ? 'SELL' : 'BUY';

  return (
    <div className={`queue-card ${isCall ? 'call-card' : 'put-card'}`}>
      <div className="queue-card-header">
        <div className="queue-card-tickers">
          {tickers}
        </div>
        <div className="queue-card-header-actions">
          <span className={`option-type-badge ${isCall ? 'call' : 'put'}`}>
            {positionLabel} {isCall ? 'CALL' : 'PUT'}
          </span>
        </div>
      </div>

      {/* 2x2 Parameter Grid matching strategy cards */}
      <div className="queue-card-params-grid">
        <div className="param-item">
          <div className="param-label">EXPIRATION</div>
          <div className="param-value">{getExpirationRangeLabel(scenario.days_until_exp)}</div>
        </div>
        <div className="param-item">
          <div className="param-label">STRIKE</div>
          <div className="param-value">{formatStrikePosition()}</div>
        </div>
        <div className="param-item">
          <div className="param-label">DAYS TO EXIT</div>
          <div className="param-value">{getDaysToExitPercent(scenario.days_to_gain, scenario.days_until_exp, scenario._exitPercent)}</div>
        </div>
        <div className="param-item">
          <div className="param-label">UNDERLYING</div>
          <div className="param-value expected-move">{formatExpectedMove()}</div>
        </div>
      </div>

      <div className="queue-card-actions">
        {onLoad && (
          <button
            onClick={onLoad}
            className={`${inlineActionButtonTemplate.cssClasses.base} ${inlineActionButtonTemplate.cssClasses.primary}`}
            title="Load this scenario into the form"
          >
            Load
          </button>
        )}
        {onSave && (
          <button
            onClick={onSave}
            className={`${inlineActionButtonTemplate.cssClasses.base} ${inlineActionButtonTemplate.cssClasses.success}`}
            title="Save this scenario"
          >
            Save
          </button>
        )}
        <button
          onClick={onRemove}
          className={`${inlineActionButtonTemplate.cssClasses.base} ${inlineActionButtonTemplate.cssClasses.danger}`}
          title="Remove from queue"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

export default QueuedScenarioCard;
